import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { API, media } from "@/lib/format";
import { STRINGS } from "./i18n";

const Ctx = createContext(null);
export const useKiosk = () => useContext(Ctx);

const CACHE_KEY = "panelo.bootstrap";
const QUEUE_KEY = "panelo.leadQueue";
export const APP_VERSION = "1.4.0";
const read = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } };
const write = (k, v) => localStorage.setItem(k, JSON.stringify(v));

export const getKioskId = () => {
  const q = new URLSearchParams(window.location.search).get("kiosk");
  if (q) localStorage.setItem("panelo.kiosk", q);
  return localStorage.getItem("panelo.kiosk") || "KSK-ANN-01";
};

const postLead = (lead) =>
  fetch(`${API}/leads`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(lead) });

export function KioskProvider({ children, live = true }) {
  const [data, setData] = useState(() => read(CACHE_KEY, null));
  const [lang, setLang] = useState("en");
  const [online, setOnline] = useState(navigator.onLine);
  const [pulse, setPulse] = useState(0);
  const [compare, setCompare] = useState([]);
  const [kioskId, setKioskIdState] = useState(getKioskId);
  const version = useRef(data?.version);
  const plays = useRef([]);
  const events = useRef(read("panelo.events", []));
  const battery = useRef(null);
  useEffect(() => {
    if (navigator.getBattery) navigator.getBattery().then((b) => { battery.current = b; }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const res = await fetch(`${API}/kiosk/bootstrap?kiosk_id=${kioskId}`);
    const json = await res.json();
    const changed = version.current != null && json.version !== version.current;
    version.current = json.version;
    setData(json);
    write(CACHE_KEY, json);
    json.models.flatMap((m) => m.colours.map((c) => c.image)).forEach((src) => { const img = new Image(); img.src = media(src); });
    if (navigator.serviceWorker?.controller) {
      json.offers.filter((o) => o.media && o.media.type !== "youtube").forEach((o) => { fetch(`${API}/campaign-media/${o.media.id}`).catch(() => {}); });
    }
    if (changed) setPulse((p) => p + 1);
  }, [kioskId]);

  const flushLeads = useCallback(async () => {
    const queue = read(QUEUE_KEY, []);
    const left = [];
    for (const lead of queue) {
      try {
        const r = await postLead(lead);
        if (r.status >= 500) left.push(lead);
      } catch { left.push(lead); }
    }
    write(QUEUE_KEY, left);
  }, []);

  const flushPlays = useCallback(async () => {
    const post = async (ref, path) => {
      if (!ref.current.length) return;
      const batch = ref.current.splice(0, 200);
      try {
        await fetch(`${API}${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(batch) });
      } catch { ref.current.unshift(...batch); }
    };
    await post(plays, "/kiosk/plays");
    await post(events, "/kiosk/events");
    write("panelo.events", events.current);
  }, []);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  useEffect(() => {
    if (!live) return undefined;
    const tick = async () => {
      try {
        const b = battery.current;
        const health = `&uptime_s=${Math.round(performance.now() / 1000)}&app_version=${APP_VERSION}` + (b ? `&battery=${Math.round(b.level * 100)}&charging=${b.charging}` : "");
        const r = await fetch(`${API}/kiosk/version?kiosk_id=${kioskId}${health}`);
        const { version: v } = await r.json();
        setOnline(true);
        if (v !== version.current) await load();
        if (read(QUEUE_KEY, []).length) flushLeads();
      } catch { setOnline(false); }
    };
    const a = setInterval(tick, 2500);
    const b = setInterval(flushPlays, 12000);
    const on = () => { setOnline(true); flushLeads(); load().catch(() => {}); };
    const off = () => setOnline(false);
    const bye = () => {
      if (!events.current.length || !navigator.sendBeacon) return;
      const ok = navigator.sendBeacon(`${API}/kiosk/events`, new Blob([JSON.stringify(events.current.slice(0, 300))], { type: "application/json" }));
      if (ok) { events.current = []; write("panelo.events", []); }
    };
    window.addEventListener("pagehide", bye);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { clearInterval(a); clearInterval(b); window.removeEventListener("pagehide", bye); window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, [live, kioskId, load, flushLeads, flushPlays]);

  const submitLead = useCallback(async (lead) => {
    const full = { ...lead, kiosk_id: kioskId, lang, client_id: crypto.randomUUID(), created_at: new Date().toISOString() };
    try {
      const r = await postLead(full);
      if (r.status === 422) return { status: "invalid", detail: (await r.json()).detail };
      if (r.status === 429) return { status: "limited", detail: (await r.json().catch(() => ({}))).detail };
      if (!r.ok) throw new Error("server");
      return { status: "sent" };
    } catch {
      write(QUEUE_KEY, [...read(QUEUE_KEY, []), full]);
      return { status: "queued" };
    }
  }, [kioskId, lang]);

  const logPlay = useCallback((content_kind, content_id, content_title) => {
    if (!live) return;
    plays.current.push({ kiosk_id: kioskId, content_kind, content_id, content_title, shown_at: new Date().toISOString(), duration_ms: 0 });
  }, [kioskId, live]);

  const logEvent = useCallback((kind, model_slug = null) => {
    if (!live) return;
    events.current.push({ kiosk_id: kioskId, kind, model_slug, at: new Date().toISOString() });
    write("panelo.events", events.current.slice(-300));
  }, [kioskId, live]);

  const setKioskId = (id) => { localStorage.setItem("panelo.kiosk", id); setKioskIdState(id); };
  const t = useCallback((k) => STRINGS[lang][k] ?? STRINGS.en[k] ?? k, [lang]);
  const tx = useCallback((obj) => (obj ? obj[lang] || obj.en : ""), [lang]);
  const cat = useCallback((c) => STRINGS[lang][`cat_${c}`] || c, [lang]);
  const resetSession = useCallback(() => { setLang("en"); setCompare([]); }, []);

  const value = { data, lang, setLang, t, tx, cat, online, pulse, compare, setCompare, submitLead, logPlay, logEvent, resetSession, kioskId, setKioskId };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
